/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./login.scss";
import { BrandButton } from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import { useNavigate } from "react-router-dom";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { z } from "zod";
import { Snackbar, Alert } from "@mui/material";
import { ApiEndpoint } from "../components/utils";
import { pageIds } from "../components/utils";
import { UserInfoRecord } from "../components/types";
import ChooseAccountType from "../components/chooseAccountType";
import { setUserInfo, getUserInfo, getAppInfo } from "../components/user";
import { Link } from "react-router-dom";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BackButton from "../components/backBtn";


interface PageResponse {
  headerTitle?: string;
  welcomeMessage?: any;
  loginFooter: any;
}

const Login = () => {
  const [data, setdata] = useState<PageResponse | null>();
  const [open, setOpen] = useState(false);
  const [snackError, setSnackError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const notEmptyString = z.string().min(1);
  const vertical = "bottom";
  const horizontal = "center";
  const validEmail = z.object({
    email: z
      .string()
      .min(1, { message: "Email field has to be filled." })
      .email("This is not a valid email."),
  });
  const username = useRef("");
  const password = useRef("");

  const { isLoading, error } = useQuery("registerPageData", async () => {
    const resp = await axios.get(
      ApiEndpoint + "wp-json/satapp-api/v1/getPageText?slug=" + pageIds.login
    );
    // TODO - on the server make exam type null by default and update to selected type that user chose.
    setdata(resp.data as PageResponse);
  });

  const setAccountType = (type: number) => {
    const userInfo = getUserInfo();
    setUserInfo({ userType: type });

    if (type === 1) {
      navigate("/dashboard", { replace: true });
    } else {
      const couponCode = getAppInfo()?.couponCode;
      navigate("/register-coupon", {
        replace: true,
        state: {
          examId: -1,
          examFee: userInfo?.fullExamFee,
          cCode: couponCode,
        },
      });
    }
  };

  const validateForm = async () => {
    const checkEmail = validEmail.safeParse({ email: username.current });
    const checkPassword = notEmptyString.safeParse(password.current);
    if (!checkEmail.success || !checkPassword.success) {
      setErrorMsg(
        "Email or password field are in incorrect format. Please try again."
      );
      setSnackError(true);
    } else {
      setLoading(true);
      try {
        const resp = await axios.post(
          ApiEndpoint + "wp-json/satapp-api/v1/login",
          {
            email: username.current,
            password: password.current,
          }
        );

        if (resp.data) {
          const success: any = resp.data.success;
          setLoading(false);
          if (success) {
            // set localstorage user info...
            const data: UserInfoRecord =
              resp.data as unknown as UserInfoRecord;
            setUserInfo(data, true);
            const couponCode = getAppInfo()?.couponCode;


            if (data.userType === -1) {
              setOpen(true);
            } else {
              if(couponCode) {
                navigate("/register-coupon", {
                  replace: true,
                  state: {
                    examId: -1,
                    examFee: data?.fullExamFee,
                    cCode: couponCode,
                  },
                });
              } else {
                navigate("/dashboard", { replace: true });
              }
            }
          } else {
            setErrorMsg(resp.data.message);
            setSnackError(true);
            return;
          }
        } else {
          setErrorMsg("Error logging in. Please try again.");
          setSnackError(true);
          return;
        }
        setLoading(false);
        return;
      } catch (e) {
        setErrorMsg("Error logging in. Please try again.");
        setSnackError(true);
        setLoading(false);
      }
    }
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        validateForm();
      }
    };
    document.addEventListener("keydown", onEnter);
    return () => {
      document.removeEventListener("keydown", onEnter);
    };
  }, []);

  if (isLoading) return <Loader />;
  if (error) return <LoadError />;

  return (
    <div className="loginPage">
      <BackButton navigate={navigate} destination="/" text="home" />
      <div className="headline">
        <h1>{data?.headerTitle}</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{ __html: data?.welcomeMessage }}
        />
      </div>
      <div className="loginForm">
        <div className="formRow">
          <BrandInput
            onChange={(evt) => (username.current = evt.currentTarget.value)}
            id="filled-basic"
            label="Email"
            variant="filled"
          />
          <BrandInput
            onChange={(evt) => (password.current = evt.currentTarget.value)}
            id="filled-bas)ic"
            label="Password"
            type="password"
            variant="filled"
          />
          <div className="btnHolder">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} />
            ) : (
              <BrandButton onClick={validateForm}>LOGIN</BrandButton>
            )}
          </div>
        </div>
        <div className="formRow forgotPassword">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>
        <div className="loginMeta">
          <div
            className="loginFooter"
            dangerouslySetInnerHTML={{ __html: data?.loginFooter }}
          />
        </div>
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
      <ChooseAccountType open={open} setAccountType={setAccountType} />
    </div>
  );
};

export default Login;
